import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { ErpType } from "src/network/User";
import styles from "./BookingTextField.module.css";
import { IAccountingsHistoryTag } from "src/stores/krediflow/InvoiceTypes";
import { BookingTextsModal } from "./BookingTextsModal/BookingTextsModal";

interface IBookingTextFieldProps {
    accountingRowIndex: number;
}

const RIMO_MAX_ROW_LENGTH = 50;

const BookingTextFieldBase = (props: IBookingTextFieldProps) => {
    const { t } = useTranslation();
    const {
        invoiceStore,
        accountingAccountSearchStore,
        accountingCostcenterStore,
        authStore,
        accountingBookingTextStore
    } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const bookingTextInputRef = useRef<HTMLTextAreaElement>(null);

    const [hasInitialized, setHasInitialized] = useState(false);
    const [focusedTagIndex, setFocusedTagIndex] = useState<number>(-1);

    useEffect(() => {
        const erpType = authStore.user?.erpType;

        if (
            accountingAccountSearchStore.triggerFocusNextInput.isActive &&
            accountingAccountSearchStore.triggerFocusNextInput.index === accountingRowIndex
        ) {
            if (erpType === ErpType.RIMO) {
                bookingTextInputRef.current?.focus();
                accountingAccountSearchStore.setTriggerFocusNextInput(false, accountingRowIndex);
            }

            if (erpType === ErpType.IT2) {
                const accountRequiredCostcenter =
                    invoiceStore.currentInvoice?.getAccountingAccount(accountingRowIndex)?.requiresCostCenter;

                if (!accountRequiredCostcenter) {
                    bookingTextInputRef.current?.focus();
                    accountingAccountSearchStore.setTriggerFocusNextInput(false, accountingRowIndex);
                }
            }
        }

        // This useEffect only listens to changes in accountingAccountSearchStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingAccountSearchStore.triggerFocusNextInput]);

    useEffect(() => {
        const erpType = authStore.user?.erpType;

        if (
            erpType === ErpType.IT2 &&
            accountingCostcenterStore.triggerFocusNextInput.isActive &&
            accountingCostcenterStore.triggerFocusNextInput.index === accountingRowIndex
        ) {
            bookingTextInputRef.current?.focus();
            accountingCostcenterStore.setTriggerFocusNextInput(false, accountingRowIndex);
        }
        // This useEffect only listens to changes in accountingCostcenterStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingCostcenterStore.triggerFocusNextInput]);

    useEffect(() => {
        if (bookingTextInputRef.current && !hasInitialized) {
            const value = bookingTextInputRef.current.value || "";
            const newlineCount = (value.match(/\n/g) || []).length;

            if (newlineCount >= 1) {
                bookingTextInputRef.current.rows = 2;
            } else {
                bookingTextInputRef.current.rows = 1;
            }

            setHasInitialized(true);
        }
        // eslint-disable-next-line
    }, [bookingTextInputRef.current, hasInitialized]);

    // Handle Ctrl+F3 to open Booking Texts Modal (if cursor is in Booking Text field and account is selected)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.ctrlKey &&
                event.key === "F3" &&
                document.activeElement === bookingTextInputRef.current &&
                invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex)
            ) {
                event.preventDefault();
                accountingBookingTextStore.setIsBookingTextsOfAccountModalDisplayed(true);
                accountingBookingTextStore.loadAccountingsHistoryDataByAccountNumber(accountingRowIndex);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [accountingBookingTextStore, accountingRowIndex, invoiceStore.currentInvoice]);


    // RIMO can handle a max of 50 (RIMO_MAX_ROW_LENGTH) characters per row
    const formatBookingTextRimo = useCallback(
        (bookingTextValue: string): string => {
            let newBookingText = bookingTextValue;

            const erpType = authStore.user?.erpType;

            if (erpType === ErpType.RIMO) {
                const newlineCountInput = (bookingTextValue.match(/\n/g) || []).length;

                if (newlineCountInput === 0 && bookingTextValue.length > RIMO_MAX_ROW_LENGTH) {
                    newBookingText =
                        bookingTextValue.slice(0, RIMO_MAX_ROW_LENGTH) +
                        "\n" +
                        bookingTextValue.slice(RIMO_MAX_ROW_LENGTH);
                }

                const lines = newBookingText.split("\n");

                if (lines.length === 2 && lines[1].length > RIMO_MAX_ROW_LENGTH) {
                    lines[1] = lines[1].slice(0, RIMO_MAX_ROW_LENGTH);
                    newBookingText = lines.join("\n");
                }
            }

            return newBookingText;
        },
        [authStore.user?.erpType]
    );

    const onChangeBookingText = useCallback(
        (bookingText: string) => {
            let newBookingText = bookingText;

            if (newBookingText.trim() === "") {
                accountingBookingTextStore.setWasAccountingsHistoryTagSelected(accountingRowIndex, false);
                accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(accountingRowIndex, false);
            }

            const erpType = authStore.user?.erpType;

            if (erpType === ErpType.RIMO) {
                newBookingText = formatBookingTextRimo(newBookingText);
            }

            const newlineCountNewBookingTextValue = (newBookingText.match(/\n/g) || []).length;

            // Check if the input has a newline and only allow one newline
            if (bookingTextInputRef.current) {
                if (newlineCountNewBookingTextValue === 0) {
                    bookingTextInputRef.current.rows = 1;
                } else if (newlineCountNewBookingTextValue === 1) {
                    bookingTextInputRef.current.rows = 2;
                }

                // If there's more than one newline, prevent adding more
                if (newlineCountNewBookingTextValue > 1) {
                    bookingTextInputRef.current.value = newBookingText.replace(/\n/g, "").replace(/$/, "\n");
                    return;
                }
            }

            invoiceStore.updateAccountingWithBookingText(newBookingText, accountingRowIndex);
        },
        [accountingRowIndex, authStore.user?.erpType, formatBookingTextRimo, invoiceStore, accountingBookingTextStore]
    );

    const onChangeBookingTextInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputBookingTextArea = e.target.value;

            onChangeBookingText(inputBookingTextArea);
        },
        [onChangeBookingText]
    );

    const onBlurBookingTextField = useCallback(() => {
        invoiceStore.currentInvoice?.validateAccountingsOnFieldLevel();
    }, [invoiceStore.currentInvoice]);

    const onKeyDownBookingTextField = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Tab" && !event.shiftKey) {
                const accountingsHistoryTags =
                    accountingBookingTextStore.displayedAccountingsHistoryTagsByIndex[accountingRowIndex];

                const areTagsDisplayed =
                    !accountingBookingTextStore.wasAccountingsHistoryTagSelected[accountingRowIndex] &&
                    accountingsHistoryTags &&
                    accountingsHistoryTags.length > 0;

                if (areTagsDisplayed) {
                    const nextTagIndex = focusedTagIndex + 1;
                    if (nextTagIndex < accountingsHistoryTags.length) {
                        // Move to next tag
                        setFocusedTagIndex(nextTagIndex);
                    } else {
                        // We've reached the end of tags, let Tab continue to next input naturally
                        setFocusedTagIndex(-1);
                    }
                    return;
                }
            }
        },
        [accountingBookingTextStore, accountingRowIndex, focusedTagIndex]
    );

    const onFocusBookingTextField = useCallback(() => {
        const textarea = bookingTextInputRef.current;
        if (textarea) {
            const textLength = textarea.value.length;

            // Use a slight delay to ensure this runs after the click event
            setTimeout(() => {
                textarea.setSelectionRange(textLength, textLength);
                textarea.focus(); // Ensure the textarea retains focus
            }, 0);
        }
    }, []);

    const onTagFocus = useCallback((index: number) => {
        setFocusedTagIndex(index);
    }, []);

    const onTagBlur = useCallback(() => {
        setFocusedTagIndex(-1);
    }, []);

    const getOnTagFocus = useCallback((index: number) => () => onTagFocus(index), [onTagFocus]);

    const onClickAccountingsHistoryTag = useCallback(
        (accountingsHistoryTagText: string) => () => {
            accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(accountingRowIndex, false);
            accountingBookingTextStore.setWasAccountingsHistoryTagSelected(accountingRowIndex, true);

            onChangeBookingText(accountingsHistoryTagText);

            // Focus and position cursor at the end
            const textarea = bookingTextInputRef.current;
            if (textarea) {
                setTimeout(() => {
                    const textLength = accountingsHistoryTagText.length;
                    textarea.setSelectionRange(textLength, textLength);
                    textarea.focus();
                }, 0);
            }
        },
        [accountingBookingTextStore, onChangeBookingText, accountingRowIndex]
    );

    const onTagKeyDown = useCallback(
        (index: number) => (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                const accountingsHistoryTags =
                    accountingBookingTextStore.displayedAccountingsHistoryTagsByIndex[accountingRowIndex];
                if (accountingsHistoryTags && accountingsHistoryTags[index]) {
                    onClickAccountingsHistoryTag(accountingsHistoryTags[index].text)();
                }
            } else if (event.key === "Escape") {
                event.preventDefault();
                setFocusedTagIndex(-1);
                bookingTextInputRef.current?.focus();
            }
        },
        [accountingBookingTextStore, accountingRowIndex, onClickAccountingsHistoryTag]
    );

    const onMoreTagsKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(accountingRowIndex, true);
            } else if (event.key === "Escape") {
                event.preventDefault();
                setFocusedTagIndex(-1);
                bookingTextInputRef.current?.focus();
            }
        },
        [accountingBookingTextStore, accountingRowIndex]
    );

    const onClickDisplayAllAccountTags = useCallback(
        (accountingRowIndex: number) => () => {
            accountingBookingTextStore.setAreAllAccountingsHistoryTagsDisplayed(accountingRowIndex, true);
        },
        [accountingBookingTextStore]
    );

    const displayBookingTextsByAccountNumberModal = useCallback(() => {
        accountingBookingTextStore.setIsBookingTextsOfAccountModalDisplayed(true);
        accountingBookingTextStore.loadAccountingsHistoryDataByAccountNumber(accountingRowIndex);
    }, [accountingBookingTextStore, accountingRowIndex]);

    const creditorId = invoiceStore.currentInvoice && invoiceStore.currentInvoice.creditor?.id;

    let accountingsHistoryTags: IAccountingsHistoryTag[] = creditorId
        ? accountingBookingTextStore.displayedAccountingsHistoryTagsByIndex[accountingRowIndex]
        : [];

    let accountingsHistoryTagElements = accountingsHistoryTags
        ? accountingsHistoryTags.map((accountingsHistoryTag, index) => {
              return (
                  <div
                      key={index}
                      className={styles.TagButton}
                      onClick={onClickAccountingsHistoryTag(accountingsHistoryTag.text)}
                      tabIndex={0}
                      onFocus={getOnTagFocus(index)}
                      onBlur={onTagBlur}
                      onKeyDown={onTagKeyDown(index)}
                  >
                      {accountingsHistoryTag.text}
                  </div>
              );
          })
        : undefined;

    const allBookingTextsForAccount = (
        <span className={styles.BookingTextsForAccountLink} onClick={displayBookingTextsByAccountNumberModal}>
            Alle Buchungstext zum Konto
        </span>
    );

    let displayAllBookingTextsForAccount = false;

    if (
        accountingBookingTextStore.hasMoreAccountingsHistoryTagsToDisplay(accountingRowIndex) &&
        accountingsHistoryTagElements
    ) {
        accountingsHistoryTagElements.push(
            <div
                key={"displayMoreTags"}
                className={styles.TagButton}
                onClick={onClickDisplayAllAccountTags(accountingRowIndex)}
                tabIndex={0}
                onKeyDown={onMoreTagsKeyDown}
            >
                ...
            </div>
        );
    } else if (invoiceStore.currentInvoice?.isAccountingAccountSelected(accountingRowIndex)) {
        displayAllBookingTextsForAccount = true;
    }

    const accountingsHistoryTagsContainerElement = (
        <>
            <div className={styles.TagContainer}>{accountingsHistoryTagElements}</div>
            {displayAllBookingTextsForAccount && allBookingTextsForAccount}
        </>
    );

    return (
        <>
            <UI.Input
                ref={bookingTextInputRef}
                label={t("screens.kredi_flow.accountings.text").toString()}
                type="text"
                as="textarea"
                autoComplete="off"
                value={invoiceStore.currentInvoice?.getAccountingBookingText(accountingRowIndex) ?? ""}
                onChange={onChangeBookingTextInput}
                onBlur={onBlurBookingTextField}
                onFocus={onFocusBookingTextField}
                onKeyDown={onKeyDownBookingTextField}
                className={styles.BookingTextArea}
            />
            {!accountingBookingTextStore.wasAccountingsHistoryTagSelected[accountingRowIndex]
                ? accountingsHistoryTagsContainerElement
                : undefined}
            <BookingTextsModal onClickBookingText={onClickAccountingsHistoryTag} />
        </>
    );
};

export const BookingTextField = Sentry.withProfiler(observer(BookingTextFieldBase));
